import { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useSalesTeamConfig } from '../hooks/useSalesTeamConfig';
import { SalesTeamConfigFilters } from '../components/sales-team-config/SalesTeamConfigFilters';
import { UnassignedManagersList } from '../components/sales-team-config/UnassignedManagersList';
import { TeamMembersList } from '../components/sales-team-config/TeamMembersList';
import { ManageTeams } from '../components/sales-team-config/ManageTeams';

type Tab = 'assignments' | 'teams';

export function SalesTeamConfigPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/sales-team-config');
    const [activeTab, setActiveTab] = useState<Tab>('assignments');
    const {
        teams, assignments, unassignedManagers, assignedByTeam, companyCodesInUse,
        loading, error,
        searchTerm, selectedTeamId, selectedCompanyCode,
        setSearchTerm, setSelectedTeamId, setSelectedCompanyCode,
        assignManager, unassignManager, createTeam, updateTeam, refresh,
    } = useSalesTeamConfig();

    const handleAssign = useCallback(async (salesManagerId: string, salesTeamId: number) => {
        await assignManager(salesManagerId, salesTeamId);
    }, [assignManager]);

    const handleUnassign = useCallback(async (salesManagerId: string) => {
        await unassignManager(salesManagerId);
    }, [unassignManager]);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const tabs: { key: Tab; label: string }[] = [
        { key: 'assignments', label: 'Manager Assignments' },
        { key: 'teams', label: 'Manage Teams' },
    ];

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Sales Team Configuration</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage sales team structure and assign managers to teams.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={refresh}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    {canEdit && (
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Edit Access
                        </span>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex gap-6">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.key
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {tab.label}
                            {tab.key === 'assignments' && unassignedManagers.length > 0 && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                                    {unassignedManagers.length}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading ? (
                <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : (
                <>
                    {activeTab === 'assignments' && (
                        <>
                            {/* Filters */}
                            <SalesTeamConfigFilters
                                searchTerm={searchTerm}
                                selectedTeamId={selectedTeamId}
                                selectedCompanyCode={selectedCompanyCode}
                                teams={teams}
                                companyCodes={companyCodesInUse}
                                onSearchChange={setSearchTerm}
                                onTeamChange={setSelectedTeamId}
                                onCompanyChange={setSelectedCompanyCode}
                            />

                            {/* Unassigned Managers */}
                            {canEdit && (
                                <UnassignedManagersList
                                    managers={unassignedManagers}
                                    teams={teams}
                                    onAssign={handleAssign}
                                />
                            )}

                            {/* Assigned Managers by Team */}
                            <TeamMembersList
                                assignedByTeam={assignedByTeam}
                                teams={teams}
                                canEdit={canEdit}
                                onAssign={handleAssign}
                                onUnassign={handleUnassign}
                            />
                        </>
                    )}

                    {activeTab === 'teams' && (
                        <ManageTeams
                            teams={teams}
                            assignments={assignments}
                            canEdit={canEdit}
                            onCreateTeam={createTeam}
                            onUpdateTeam={updateTeam}
                        />
                    )}
                </>
            )}
        </div>
    );
}
